import * as Dialog from "@radix-ui/react-dialog";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ComponentPropsWithRef, ElementRef } from "react";
import { cn } from "../../helpers/styles";
import { DialogTitle } from "../Dialog/Dialog";

// See Modal.stories.tsx/BasicExample for recommended usage

export const ModalRoot = Dialog.Root;
export const ModalTitle = DialogTitle;

export function ModalOverlay({
	children,
	className,
	ref,
	...props
}: ComponentPropsWithRef<typeof Dialog.Overlay> & {
	ref?: React.Ref<ElementRef<typeof Dialog.Overlay>>;
}) {
	return (
		<Dialog.Overlay
			ref={ref}
			className={cn(
				"fixed inset-0 z-[100] bg-black opacity-25 duration-300 animate-in fade-in",
				className,
			)}
			{...props}
		>
			{children}
		</Dialog.Overlay>
	);
}

ModalOverlay.displayName = "ModalOverlay";

export function ModalContent({
	children,
	className,
	size = "small",
	alignment = "center",
	ref,
	...props
}: ComponentPropsWithRef<typeof Dialog.Content> &
	VariantProps<typeof modalContentVariants> & {
		ref?: React.Ref<ElementRef<typeof Dialog.Content>>;
	}) {
	return (
		<Dialog.Content
			ref={ref}
			className={cn(modalContentVariants({ size, alignment }), className)}
			{...props}
		>
			{children}
		</Dialog.Content>
	);
}

ModalContent.displayName = "ModalContent";

const modalContentVariants = cva(
	"fixed z-[100] flex max-h-fit w-full flex-col gap-xl overflow-hidden rounded-t-xl bg-white p-xl shadow-3 duration-300 animate-in fade-in zoom-in focus:outline-none max-lg:bottom-0 max-lg:translate-y-0 lg:flex-row lg:rounded-xl",
	{
		variants: {
			size: {
				small: "lg:w-[640px]",
				medium: "max-sm:min-h-screen sm:w-10/12",
				large: "min-h-screen",
			},
			alignment: {
				center:
					"lg:left-1/2 lg:-translate-x-1/2 lg:top-1/2 lg:-translate-y-1/2 lg:slide-in-from-left-1/2 lg:slide-in-from-top-0",
				topRight: "max-sm:left-0 lg:right-xl lg:top-xl",
			},
		},
		defaultVariants: {
			alignment: "center",
		},
	},
);

export function ModalHeader({
	children,
	className,
	ref,
	...props
}: ComponentPropsWithRef<"div"> & {
	ref?: React.Ref<HTMLDivElement>;
}) {
	return (
		<div ref={ref} className={cn("flex items-center max-sm:justify-between", className)} {...props}>
			{children}
		</div>
	);
}

ModalHeader.displayName = "ModalHeader";

export function ModalClose({
	children,
	className,
	ref,
	...props
}: ComponentPropsWithRef<typeof Dialog.Close> & {
	ref?: React.Ref<ElementRef<typeof Dialog.Close>>;
}) {
	return (
		<Dialog.Close
			ref={ref}
			className={cn("absolute right-xl top-xl flex cursor-pointer", className)}
			{...props}
		>
			{children}
		</Dialog.Close>
	);
}

ModalClose.displayName = "ModalClose";

export function ModalMain({
	children,
	className,
	ref,
	...props
}: ComponentPropsWithRef<"div"> & {
	ref?: React.Ref<HTMLDivElement>;
}) {
	return (
		<div ref={ref} className={cn("w-full", className)} {...props}>
			{children}
		</div>
	);
}

ModalMain.displayName = "ModalMain";

export function ModalPortal({
	children,
	ref,
	...props
}: ComponentPropsWithRef<typeof Dialog.Portal> & {
	ref?: React.Ref<HTMLDivElement>;
}) {
	return (
		<div ref={ref}>
			<Dialog.Portal {...props}>{children}</Dialog.Portal>
		</div>
	);
}

ModalPortal.displayName = "ModalPortal";
